import React, {Fragment, ReactElement, useEffect, useState} from "react"
import {Button, ConfigProvider, Input, List as AntList} from "antd"
import {AxiosResponse} from "axios"
import {PlusOutlined} from "@ant-design/icons"
import dayjs from "dayjs"
import {observer} from "mobx-react-lite"
import classNames from "classnames"
import {t} from "@ui/common/intl"
import {useStore} from "@ui/stores"
import {pageSize, search, TUserTrack} from "@ui/api/track"
import EyeOffIcon from "@ui/icons/track/EyeOff"
import EyeIcon from "@ui/icons/track/Eye"
import BookmarkIcon from "@ui/icons/track/Bookmark"
import BookmarkOffIcon from "@ui/icons/track/BookmarkOff"
import SearchIcon from "@ui/icons/common/Search"
import ErrorSearch from "@ui/icons/track/ErrorSearch"
import Loader from "@ui/components/common/Loader"
import SwipePanel, {SwipePanelHeight} from "../common/SwipePanel"
import EmptySearchResult from "../common/EmptySearchResult"
import {TrackPage, TTrackPageProps} from "./"

const List = ({notifyError, goto, close, isShow}: TTrackPageProps): ReactElement => {
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)
  const {track, route} = useStore()

	useEffect(() => {
    if (isShow && (route.empty === undefined || route.state.reload)) load()
	}, [isShow])

	const load = async (page: number = 0) => {
    setLoading(true)
    return await search({page, filter: route.filter.params})
      .then(data => {
        if (page === 0) {
          route.clear()
          if (!route.filter.is()) route.setEmpty(!data.length)
        }
        route.changeState({more: data.length === pageSize})
        route.appendSearch(data)
      })
      .catch((error: AxiosResponse) => {
        setError(true)
        notifyError("track.list.search.error", error)
        route.changeState({more: false})
      })
      .finally(() => setLoading(false))
	}

	const handleNext = () => load(Math.floor((route.tracks || []).length / pageSize))

	const handleSearch = (search: string) => {
    route.filter.change({name: search})
    load()
  }

	const handleAdd = () => goto(TrackPage.Start)

	const handleClose = () => {
    close()
    route.hideAll()
    route.filter.clear()
    route.changeState({reload: true})
	}

	const handleShow = (route: TUserTrack) => goto(TrackPage.View, route)

	const handleFilterMark = () => {
    route.filter.change({favorite: !route.filter.params.favorite || undefined})
    load()
  }

	const handleHideAll = () => route.hideAll()

  const handleSearchAgain = () => {
    setError(false)
    load()
  }

  return isShow
    ? (<SwipePanel
        onClose={handleClose}
        onSwipe={close}
        className="list"
        height={route.empty === true ? SwipePanelHeight.FITCONTENT : SwipePanelHeight.FULL}
        header={
          <>
            <span>{t("track.list.title")}</span>
            {route.empty === false && !track.isRecording &&
              <Button
                className="button-regular add-button"
                shape="round"
                icon={<PlusOutlined/>}
                onClick={handleAdd}
              >
                {t("button.add")}
              </Button>
            }
          </>
        }
      >
        {loading && <Loader />}
        {error
          ? (<div className="search-error">
              <div className="error-text">
                <ErrorSearch/>
                <span className="primary-text">{t("track.list.search.error")}</span>
              </div>
              <div className="reload-button-container">
                <Button className="button-primary" onClick={handleSearchAgain}>
                  {t("track.list.search.again")}
                </Button>
              </div>
            </div>)
          : (<Fragment>
              {route.empty === false && (
                <Fragment>
                  <div className="filter">
                    <Input.Search
                      placeholder={t("track.list.filter.placeholder")}
                      addonAfter={null}
                      prefix={<SearchIcon/>}
                      value={route.filter.params.name}
                      onChange={(e) => route.filter.change({name: e.target.value})}
                      onSearch={handleSearch}
                      allowClear={true}
                      maxLength={50}
                    />
                    <Button
                      icon={route.filter.params.favorite ? <BookmarkIcon/> : <BookmarkOffIcon/>}
                      onClick={handleFilterMark}
                    />
                  </div>
                  <ConfigProvider renderEmpty={() => <EmptySearchResult/>}>
                    <AntList
                      dataSource={route.tracks || []}
                      loadMore={route.state.more && <Button block onClick={handleNext}>...</Button>}
                      renderItem={(item) => (
                        <AntList.Item
                          key={item.id}
                          onClick={() => handleShow(item)}
                          actions={[
                            item.favorite ? <BookmarkIcon/> : <BookmarkOffIcon/>,
                            route.isDisplay(item.id) ? <EyeIcon/> : <EyeOffIcon/>
                          ]}
                        >
                          <AntList.Item.Meta
                            avatar={<span
                              className={classNames("track-list-color", `color-${item.color || 1}`)}></span>}
                            title={<span className="list-header-text">{item.name}</span>}
                            description={<span
                              className="list-text ">{dayjs(item.end).format('DD.MM.YYYY HH:mm')}</span>}
                          />
                        </AntList.Item>
                      )}
                    />
                  </ConfigProvider>
                  {route.isDisplay() &&
                    <div className="hide-all-button-container">
                      <Button className="button-regular" onClick={handleHideAll}>
                        {t("track.list.hide.all.tracks.button")}
                      </Button>
                    </div>
                  }
                </Fragment>
              )}
              {route.empty === true && (
                <Fragment>
                  <div className="no-tracks-text primary-text">{t("track.list.empty.placeholder")}</div>
                  <Button disabled={track.isRecording} className="add-button button-primary" icon={<PlusOutlined/>} onClick={handleAdd}>
                    {t("button.add")}
                  </Button>
                </Fragment>
              )}
            </Fragment>)
        }
      </SwipePanel>)
    : (<></>)
}

export default observer(List)
